import React from "react";
import type { ReactElement, ReactNode } from "react";
import { Clock } from "lucide-react";
import { EmptyState } from "@ndd/ui";

/*
 * The RD Snapshot and RD Metrics state for an RD the latest published metric
 * batch does not cover (NO-618). Composed from the shared EmptyState block
 * rather than a bespoke panel, so it carries the design system's empty-state
 * grammar; the approved frames were updated to match.
 *
 * Neutral tone on purpose. Amber is a risk worth knowing about and red is a
 * problem the supervisor can act on; an RD who simply joined between weekly
 * publications is neither, and must never read as an alarm or as an error.
 *
 * Relative imports and `import React` so it loads under `tsx --test`. Actions
 * arrive as a node, so the router-aware `Link` lives in
 * `metrics-not-published-actions.tsx` and this file stays renderable without a
 * router.
 */

export const NOT_PUBLISHED_TITLE = "Metric data is not yet available";

/** No date: naming the next cutoff needs a publication schedule that does not exist yet. */
export function notPublishedDescription(name: string, detail: boolean): string {
  const what = detail ? "metrics and history" : "metrics";

  return `${name} has no published metrics yet. Their first ${what} arrive with the next weekly batch. Nothing is missing or broken.`;
}

export function MetricsNotPublished({
  name,
  detail = false,
  actions,
}: {
  name: string;
  /** True on the Metrics page, whose content would also include history. */
  detail?: boolean;
  actions?: ReactNode;
}): ReactElement {
  return (
    <EmptyState
      icon={Clock}
      tone="neutral"
      title={NOT_PUBLISHED_TITLE}
      description={notPublishedDescription(name, detail)}
      actions={actions}
    />
  );
}
